// Endosymbiont power (amoeba-powers.md): N = level tiny organelles orbit the
// player, biting predators on contact and starting absorbs on microbes they
// brush. Orbiter entities are children — maintained to match the power level,
// gone on respawn with the rest of the world.

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { morphologyFrom } from '../creature-recipe'
import { powerLevel, POWER_DEFS } from '../powers'
import { dropDnaForPredator, chainSegmentVulnerable, damageChainSegment } from './damage'
import { spawnInkSplatter } from './particles'
import { playSfx, panFor } from '../../engine/audio'
import { attachEyes } from './eyes'
import { T } from '../tunables'

function spawnOrbiter(world: GameWorld, player: Entity, angle: number): void {
  const c = T.powers.endosymbiont
  const orbiter: Entity = {
    position: { x: player.position.x, y: player.position.y },
    velocity: { x: 0, y: 0 },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 2.2, amp: 0.1 },
    renderable: {
      shape: 'microbe',
      radius: c.orbiterRadius,
      tint: POWER_DEFS['endosymbiont'].tint,
    },
    // FEAT_MITO: an actual mitochondrion — bean + cristae (amoeba-powers §04).
    morphology: morphologyFrom({ body: 'mito' }, Math.random()),
    orbiter: { angle, hitCooldown: 0 },
  }
  // Eyes rule (visual direction 2026-07): orbiters are agents — helpers you
  // keep, not food. Tiny oversized-for-body eyes, chibi register.
  attachEyes(orbiter, [
    { offset: { x: -0.3, y: 0.2 }, radius: 0.34 },
    { offset: { x: 0.34, y: 0.22 }, radius: 0.3 },
  ])
  world.add(orbiter)
}

export function orbitersSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  void events
  const c = T.powers.endosymbiont
  const level = powerLevel(player, 'endosymbiont')
  const orbiters = [...world.with('orbiter', 'position', 'renderable')]

  // Maintain count = level (only grows mid-run; respawn clears the world).
  for (let i = orbiters.length; i < level; i++) {
    spawnOrbiter(world, player, Math.random() * Math.PI * 2)
  }
  if (orbiters.length === 0 && level === 0) return

  const pr = player.renderable?.radius ?? T.player.baseRadius
  const orbitR = pr * c.orbitRadiusMult
  const n = Math.max(1, orbiters.length)
  const predators = [...world.with('predator', 'position', 'renderable', 'health')]
  const chainSegments = [...world.with('chainSegment', 'position', 'renderable', 'health')]

  for (let i = 0; i < orbiters.length; i++) {
    const orb = orbiters[i]
    const o = orb.orbiter!
    o.angle += c.orbitSpeed * dt
    o.hitCooldown = Math.max(0, o.hitCooldown - dt)
    // Even spacing: each orbiter keeps its own phase plus a slot offset.
    const a = o.angle + (i / n) * Math.PI * 2
    orb.position.x = player.position.x + Math.cos(a) * orbitR
    orb.position.y = player.position.y + Math.sin(a) * orbitR

    // Bite predators on contact.
    if (o.hitCooldown === 0) {
      for (const pred of predators) {
        if (pred.absorbing || pred.chainSegment) continue
        const dx = pred.position.x - orb.position.x
        const dy = pred.position.y - orb.position.y
        const reach = pred.renderable.radius + orb.renderable!.radius
        if (dx * dx + dy * dy < reach * reach) {
          pred.health!.current -= c.damage
          pred.hitFlash = T.damage.hitFlashDuration
          events.emit({ type: 'damage.dealt', target: pred, amount: c.damage })
          o.hitCooldown = c.hitCooldown
          spawnInkSplatter(world, pred.position.x, pred.position.y)
          playSfx('ally_kill', { volume: 0.18, pan: panFor(pred.position.x, player.position.x) })
          if (pred.health!.current <= 0) {
            dropDnaForPredator(world, pred)
            if (world.id(pred) !== undefined) world.remove(pred)
          }
          break
        }
      }
    }

    // Bite vulnerable boss chain segments too (monorail-228 — weapons opened
    // up beyond the dash). Shares the per-orbiter hitCooldown so a single
    // orbiter can't multi-hit; chip stays below dash via weaponChipMult.
    if (o.hitCooldown === 0) {
      for (const seg of chainSegments) {
        if (!chainSegmentVulnerable(seg)) continue
        const dx = seg.position.x - orb.position.x
        const dy = seg.position.y - orb.position.y
        const reach = seg.renderable!.radius + orb.renderable!.radius
        if (dx * dx + dy * dy < reach * reach) {
          damageChainSegment(world, seg, c.damage * T.boss.chain.weaponChipMult, events)
          o.hitCooldown = c.hitCooldown
          playSfx('ally_kill', { volume: 0.18, pan: panFor(seg.position.x, player.position.x) })
          break
        }
      }
    }

    // Brush microbes into an absorb (the body drinks them in as usual).
    for (const e of world.with('consumable', 'position', 'renderable')) {
      if (e.absorbing || e.recruitable) continue
      const dx = e.position.x - orb.position.x
      const dy = e.position.y - orb.position.y
      const reach = e.renderable.radius + orb.renderable!.radius
      if (dx * dx + dy * dy < reach * reach) {
        world.addComponent(e, 'absorbing', {
          progress: 0,
          duration: 0.20,
          startRadius: e.renderable.radius,
          startPos: { x: e.position.x, y: e.position.y },
        })
      }
    }
  }
}
